import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiGet } from '../../api/client';

/**
 * "Search IGDB" button + popup. Picking a game calls onSelect with
 * { name, cover, year, platforms } so the form can fill the poster (and title/year if empty).
 */
export default function IgdbSearch({ initialQuery = '', onSelect, label = 'Search IGDB' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestRef = useRef(0);

  const openSearch = () => {
    setQuery(initialQuery.trim());
    setResults([]);
    setError('');
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    const requestId = ++requestRef.current;
    setLoading(true);
    const timer = setTimeout(() => {
      apiGet(`/admin/igdb?q=${encodeURIComponent(term)}`)
        .then((data) => {
          if (requestId !== requestRef.current) return;
          setResults(data.results || []);
          setError('');
        })
        .catch((err) => {
          if (requestId === requestRef.current) setError(err.message);
        })
        .finally(() => {
          if (requestId === requestRef.current) setLoading(false);
        });
    }, 350);
    return () => clearTimeout(timer);
  }, [open, query]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open]);

  const pick = (game) => {
    onSelect(game);
    setOpen(false);
  };

  return (
    <>
      <button type="button" className="btn btn-secondary btn-sm admin-igdb-btn" onClick={openSearch}>
        <i className="fas fa-search" aria-hidden="true" /> {label}
      </button>
      {open && createPortal(
        <div className="admin-page admin-media-modal admin-igdb-modal" role="dialog" aria-modal="true" aria-label="Search IGDB">
          <div className="admin-media-modal__backdrop" onClick={() => setOpen(false)} />
          <div className="admin-media-modal__panel">
            <div className="admin-media-modal__header">
              <h4><i className="fas fa-gamepad" aria-hidden="true" /> Find a game on IGDB</h4>
              <button type="button" className="admin-media-modal__close" onClick={() => setOpen(false)} aria-label="Close">
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="admin-search admin-igdb-modal__search">
              <i className={loading ? 'fas fa-spinner fa-spin' : 'fas fa-search'} aria-hidden="true" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    if (results[0]) pick(results[0]);
                  }
                }}
                placeholder="Type a game name…"
                autoFocus
              />
            </div>
            {error && <p className="admin-message admin-message--error">{error}</p>}
            {!error && query.trim().length < 2 && <p className="admin-media-modal__empty">Type at least two letters to search.</p>}
            {!error && !loading && query.trim().length >= 2 && results.length === 0 && (
              <p className="admin-media-modal__empty">No games found.</p>
            )}
            {results.length > 0 && (
              <div className="admin-igdb-grid">
                {results.map((game) => (
                  <button key={game.id} type="button" className="admin-igdb-item" onClick={() => pick(game)} title={game.name}>
                    {game.cover ? (
                      <img src={game.cover} alt="" loading="lazy" />
                    ) : (
                      <span className="admin-igdb-item__empty"><i className="fas fa-image" aria-hidden="true" /> No cover</span>
                    )}
                    <strong>{game.name}</strong>
                    <span>{[game.year, game.platforms.slice(0, 3).join(', ')].filter(Boolean).join(' · ')}</span>
                  </button>
                ))}
              </div>
            )}
            <p className="admin-igdb-modal__hint">Click a game to use its cover. Press Enter to take the first result.</p>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
